import React, { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';

// MUI Components
import {
  Box,
  Typography,
  Link,
  List,
  ListItem,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,

} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import CircularProgress from '@mui/material/CircularProgress';

// MUI Icons
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DownloadIcon from '@mui/icons-material/Download';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CloseIcon from '@mui/icons-material/Close';

// Services
import { fetchComplianceBundleUrl } from '../utils/batchResultsService';
import { Authority } from '../utilities/constants';
import CustomCredentialsProvider from '../utilities/CustomCredentialsProvider';

// Images
import mdcLogo from '../assets/mdc-logo-white.svg';
import gradientImg from '../assets/Gradient.svg';
import bottomGradient from '../assets/bottom_gradient.svg';

// Styled Components
import { styled } from '@mui/system';

const StyledLink = styled(Link)(() => ({
  color: 'var(--mdc-primary)',
  textDecoration: 'underline',
  component: 'a',
  '&:hover': {
    color: 'var(--mdc-primary-active)',
  },
}));

const StyledEmailLink = styled(Link)(() => ({
  color: 'var(--mdc-primary)',
  textDecoration: 'none',
  component: 'a',
  '&:hover': {
    color: 'var(--mdc-primary-active)',
    textDecoration: 'underline',
  },
}));

const SmallFiberManualRecordIcon = styled(FiberManualRecordIcon)(({ size }) => ({
  fontSize: size || '8px',
}));

const GradientBox = styled(Box)(({ theme }) => ({
  backgroundImage: `url(${gradientImg})`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  backgroundSize: 'cover',
  padding: theme.spacing(8),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: theme.spacing(1),
}));

const LandingPage = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    if (auth.isLoading) return;
    if (auth.isAuthenticated) {
      navigate('/app', { replace: true });
    }
  }, [auth.isLoading, auth.isAuthenticated, navigate]);

  const handleSignIn = () => {
    setLoading(true);
    // Introduce a 1-second delay before redirecting
    setTimeout(() => {
      auth.signinRedirect();
      // No need to reset loading here as redirect will occur
    }, 1000);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  if (auth.isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={50} thickness={5} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top Bar */}
      <Box
        sx={{
          backgroundColor: 'var(--mdc-primary)',
          height: '36px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      />

      {/* Bottom Gradient */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          top: '50%',
          backgroundImage: `url(${bottomGradient})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          zIndex: -1,
        }}
      />

      {/* Dark Section with Main Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          backgroundColor: 'var(--mdc-soft-black)',
          color: '#fff',
          minHeight: '65vh',
          alignItems: 'center',
          pb: 4,
          flexGrow: 1,
          flexWrap: 'wrap',
        }}
      >
        {/* Left Side: Text */}
        <Box
          sx={{
            flex: 1,
            padding: '0 4%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            minWidth: '300px',
          }}
        >
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
            PDF Accessibility Remediation
          </Typography>

          <Typography variant="h5" component="h2" sx={{ fontWeight: 'medium', mb: 2 }}>
            About this solution:
          </Typography>
          <Typography variant="body1" component="h3" paragraph>
            Fix My PDF is a tool provided by the Miami-Dade County Information
            Technology Department to help county staff bring PDF documents into
            compliance with accessibility standards.
          </Typography>
          <Typography variant="body1" component="h3" paragraph>
            With the Department of Justice's April 2024 updates to how the
            Americans with Disabilities Act (ADA) will be regulated, this
            scalable solution quickly and efficiently brings PDF documents into
            compliance with WCAG 2.1 Level AA standards.
          </Typography>
          <Typography variant="body1" component="h3" paragraph>
            To get started,{' '}
            <Box component="span" sx={{ color: '#FFFFFF', fontWeight: 'bold' }}>
              click the button to the right
            </Box>{' '}
            to log in with your county credentials, upload your document, and
            receive your remediated PDF in return.
          </Typography>

          {/* Provided By Section */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mt: 4,
            }}
          >
            <Typography variant="body1" component="h3" sx={{ mr: 2, fontWeight: 'bold' }}>
              Provided by:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img
                src={mdcLogo}
                alt="Miami-Dade County Logo"
                style={{ height: 40, width: 'auto', marginRight: '16px' }}
              />
              <Typography variant="body2" sx={{ color: '#ccc' }}>
                Communications, Information &amp; Technology
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Right Side: Gradient + Button + Link in the dark area */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            minWidth: '300px',
          }}
        >
          {/* Gradient box with the button */}
          <GradientBox>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                mb: 4,
                color: '#FFFFFF',
                textAlign: 'center',
                fontWeight: 'bold',
              }}
            >
              READY TO TRANSFORM YOUR PDF?
            </Typography>
            <LoadingButton
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIosIcon />}
              onClick={handleSignIn}
              loading={loading}
              component="button"
              loadingIndicator={
                <CircularProgress
                  size={24}
                  sx={{ color: '#fff' }}
                />
              }
              sx={{
                backgroundColor: 'var(--mdc-primary)',
                color: '#FFFFFF',
                fontWeight: 'bold',
                fontSize: '1.2rem',
                width: 350,
                height: 50,
                overflow: 'hidden',
                position: 'relative',
                borderRadius: '25px',
                transition: 'transform 0.2s, background-color 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  backgroundColor: 'var(--mdc-primary-hover)',
                  transform: 'scale(1.05)',
                },
                '&.MuiLoadingButton-loading': {
                  backgroundColor: 'var(--mdc-primary)',
                },
              }}
            >
              Login and Remediate My PDF
            </LoadingButton>
          </GradientBox>

          {/* Link placed outside the GradientBox but still in the dark area */}
          <Box sx={{ mt: 2 }}>
            <StyledLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleOpenDialog();
              }}
              sx={{ fontSize: '0.9rem', color: '#FFFFFF' }}
            >
              Learn more about the remediation process
            </StyledLink>
          </Box>

          {/* Batch Results Button */}
          <Box sx={{ mt: 3 }}>
            <LoadingButton
              variant="outlined"
              size="large"
              onClick={() => {
                if (auth.isAuthenticated) {
                  navigate('/batch-results');
                } else {
                  setLoading(true);
                  sessionStorage.setItem('returnTo', '/batch-results');
                  setTimeout(() => {
                    auth.signinRedirect();
                  }, 500);
                }
              }}
              sx={{
                color: '#FFFFFF',
                backgroundColor: '#669933',
                borderColor: '#669933',
                fontWeight: 'bold',
                fontSize: '0.95rem',
                width: 350,
                height: 45,
                borderRadius: '25px',
                transition: 'transform 0.2s, background-color 0.2s',
                '&:hover': {
                  backgroundColor: '#557a2b',
                  borderColor: '#557a2b',
                  transform: 'scale(1.03)',
                },
              }}
            >
              Check Department Batch Results
            </LoadingButton>
          </Box>

          {/* Download Compliance Report Button */}
          <Box sx={{ mt: 2 }}>
            <LoadingButton
              variant="outlined"
              size="large"
              startIcon={<DownloadIcon />}
              loading={downloadingReport}
              onClick={async () => {
                if (!auth.isAuthenticated) {
                  setLoading(true);
                  sessionStorage.setItem('returnTo', '/batch-results');
                  setTimeout(() => { auth.signinRedirect(); }, 500);
                  return;
                }
                setDownloadingReport(true);
                try {
                  const provider = new CustomCredentialsProvider();
                  provider.loadFederatedLogin({ domain: Authority, token: auth.user?.id_token });
                  const { credentials: c } = await provider.getCredentialsAndIdentityId();
                  const creds = {
                    accessKeyId: c.accessKeyId,
                    secretAccessKey: c.secretAccessKey,
                    sessionToken: c.sessionToken,
                  };
                  const url = await fetchComplianceBundleUrl(creds);
                  if (url) {
                    window.open(url, '_blank');
                  } else {
                    alert('No compliance report available yet.');
                  }
                } catch {
                  alert('Failed to download compliance report.');
                } finally {
                  setDownloadingReport(false);
                }
              }}
              sx={{
                color: '#FFFFFF',
                borderColor: 'rgba(255,255,255,0.5)',
                fontWeight: 'bold',
                fontSize: '0.95rem',
                width: 350,
                height: 45,
                borderRadius: '25px',
                transition: 'transform 0.2s, background-color 0.2s',
                '&:hover': {
                  borderColor: '#FFFFFF',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  transform: 'scale(1.03)',
                },
              }}
            >
              Download Compliance Report
            </LoadingButton>
          </Box>
        </Box>
      </Box>

      {/* Accent Line */}
      <Box
        sx={{
          height: '5px',
          backgroundColor: 'var(--mdc-primary)',
        }}
      />

      {/* Support Resources Section */}
      <Box
        sx={{
          p: 4,
          borderTop: '1px solid #ddd',
          borderBottom: '1px solid #ddd',
        }}
      >
        <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
          Support resources:
        </Typography>
        <List>
          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemIcon sx={{ minWidth: '24px' }}>
              <SmallFiberManualRecordIcon size="12px" sx={{ color: '#000000' }} />
            </ListItemIcon>
            <Typography variant="body1" component="h3">
              Have questions or need support? Contact the CITD Innovation Team:{' '}
              <StyledEmailLink href="mailto:innovation@miamidade.gov">
                innovation@miamidade.gov
              </StyledEmailLink>
            </Typography>
          </ListItem>
          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemIcon sx={{ minWidth: '24px' }}>
              <SmallFiberManualRecordIcon size="12px" sx={{ color: '#000000' }} />
            </ListItemIcon>
            <Typography variant="body1" component="h3">
              This solution uses automated AI-powered remediation to bring your
              PDF documents into WCAG 2.1 Level AA compliance.
            </Typography>
          </ListItem>
          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemIcon sx={{ minWidth: '24px' }}>
              <SmallFiberManualRecordIcon size="12px" sx={{ color: '#000000' }} />
            </ListItemIcon>
            <Typography variant="body1" component="h3">
              For more information about Miami-Dade County's accessibility
              initiatives, visit{' '}
              <StyledLink
                href="https://www.miamidade.gov"
                target="_blank"
                rel="noopener"
              >
                miamidade.gov
              </StyledLink>
            </Typography>
          </ListItem>
        </List>
      </Box>

      {/* About This Solution Section */}
      <Box
        sx={{
          p: 4,
          backgroundColor: '#FAFAFA',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '-20%',
            left: '-10%',
            width: '120%',
            height: '150%',
            backgroundImage: `url(${bottomGradient})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            transform: 'rotate(-15deg)',
            opacity: 0.2,
            zIndex: -1,
          }}
        />
        <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
          About This Solution:
        </Typography>
        <Typography variant="body1" component="h3" paragraph>
          Fix My PDF is provided by the Miami-Dade County Communications, Information
          &amp; Technology Department (CITD) as part of the county's commitment to digital accessibility
          and ADA compliance.
        </Typography>
        <Typography variant="body1" component="h3" paragraph>
          The tool uses advanced AI and cloud services to automatically
          remediate PDF documents, fixing common accessibility issues such as
          missing tags, incorrect reading order, alt text generation, and more.
        </Typography>
        <Typography variant="body1" component="h3" paragraph>
          For questions or feedback, contact the CITD Innovation Team at{' '}
          <StyledEmailLink href="mailto:innovation@miamidade.gov">
            innovation@miamidade.gov
          </StyledEmailLink>
          .
        </Typography>
      </Box>

      {/*
        Dialog (modal) for remediation process
      */}
      <Dialog
  open={openDialog}
  onClose={handleCloseDialog}
  aria-labelledby="remediation-dialog-title"
>
  <DialogTitle
    id="remediation-dialog-title"
    sx={{ pr: 4, position: 'relative' }}
  >
    <strong>Remediation Process</strong>
    <IconButton
      aria-label="close"
      onClick={handleCloseDialog}
      sx={{
        position: 'absolute',
        right: 8,
        top: 8,
        color: (theme) => theme.palette.grey[500],
      }}
    >
      <CloseIcon />
    </IconButton>
  </DialogTitle>

  <DialogContent dividers>
    <Typography variant="body1" component="p"  paragraph>
      Here's how our PDF Remediation process works:
    </Typography>

    <Typography variant="body2" component="p" paragraph>
      1. <strong>Upload a Document:</strong> Once you are logged in,
      simply select a PDF to upload for remediation.
    </Typography>
    <Typography variant="body2" component="p" paragraph>
      2. <strong>Remediation:</strong> We use an automated approach,
      supported by Adobe's API and AWS services, to fix common accessibility
      issues like missing tags, incorrect reading order, Alt Text and more.
    </Typography>
    <Typography variant="body2" component="p" paragraph>
      3. <strong>Download Your Accessible PDF:</strong> Within a short
      time, you'll receive your remediated PDF and accessibility reports ready to share with
      everyone.
    </Typography>

    {/* Additional Restrictions Section */}
    <Typography variant="body1" component="p" paragraph sx={{ mt: 2 }}>
      <strong>Please note the following restrictions before uploading:</strong>
    </Typography>
    <Typography variant="body2" component="p" paragraph>
      1. Each user is limited to <strong>100</strong> PDF document uploads.
    </Typography>
    <Typography variant="body2" component="p" paragraph>
      2. Documents cannot exceed <strong>250</strong> pages.
    </Typography>
    <Typography variant="body2" component="p" paragraph>
      3. Documents must be smaller than <strong>250</strong> MB.
    </Typography>
    <Typography variant="body2" component="p" paragraph>
      4. Do not upload documents containing <strong>sensitive information</strong>.
    </Typography>
    <Typography variant="body2" component="p" paragraph>
      5. Do not <strong>bulk upload</strong> documents.
    </Typography>
    <Typography variant="body2" component="p" paragraph>
      6. Complete remediation for <strong>one document</strong> before adding a new one.
    </Typography>
    <Typography variant="body2" component="p" paragraph>
      7. This solution only remediates <strong>PDF documents</strong>. Other document types will not be accepted.
    </Typography>
    <Typography variant="body2" component="p" paragraph>
      8. This solution does not remediate for <strong>fillable forms and color selection/contrast for people with color blindness</strong>.
    </Typography>
  </DialogContent>
</Dialog>
    </Box>
  );
};

export default LandingPage;
